import cx from 'classnames';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {CSSTransition} from 'react-transition-group';
import {storeToolTipToHide} from '~/CreateFabricInvitation/utils/tooltip';
import {BottomToolbar} from '~/common/components/BottomToolbar/BottomToolbar';
import {Button} from '~/common/FabricInvitation/EventCreateDesign/components/CardDesignNavigator/Button';
import {Tooltip} from '~/common/FabricInvitation/EventCreateDesign/components/Tooltip/Tooltip';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {useEnvelopeContext} from '~/common/FabricInvitation/EventCreateDesign/contexts/EnvelopeContext';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {selectIsPremium} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectDesignHasLogo,
  selectDesignHasLogoAndUyo,
  selectEditingMobile,
  selectIsFilledUyo,
  selectIsFullDyo,
  selectIsLogoFilled,
  selectIsUyo,
  selectPremiumFeaturesWarning,
  selectPristineEnvelopeDesign,
  selectShowMatchingSetPreview,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {shouldUseNamedStepsNav} from '~/common/utils/event_utils';
import {useIsExtraLargeAndUp, useIsLargishAndUp, useMatchQuery} from '~library/hooks/useMatchQuery';
import {AddPhotosLineIcon} from '~library/svg/AddPhotosLineIcon';
import {RevealIcon} from '~library/svg/RevealIcon';
import {DiagonalLinesIcon} from '~library/svg/v2/DiagonalLinesIcon';
import {DocumentIcon} from '~library/svg/v2/DocumentIcon';
import {MailIcon} from '~library/svg/v2/MailIcon';
import {charcoal, pebble} from '~libSass/base/_exports.sass';

import './CardDesignNavigator.sass';

const ENVELOPE_TOOLTIP_ID = 'premium-features-warning_envelope_tooltip';

export const CardDesignNavigator = ({vertical = false}) => {
  const containerRef = useRef(null);
  const isPremium = useSelector(selectIsPremium);
  const premiumFeaturesWarning = useSelector(selectPremiumFeaturesWarning);
  const isUyo = useSelector(selectIsUyo);
  const designHasLogo = useSelector(selectDesignHasLogo);
  const designHasLogoAndUyo = useSelector(selectDesignHasLogoAndUyo);
  const isFilledUyo = useSelector(selectIsFilledUyo);
  const isLogoFilled = useSelector(selectIsLogoFilled);
  const pristineEnvelopeDesign = useSelector(selectPristineEnvelopeDesign);
  const isFullDyo = useSelector(selectIsFullDyo);
  const {cardDesignRef, canvas, setActiveObjects} = useContext(EventCreateContext);
  const editingMobile = useSelector(selectEditingMobile);
  const showMatchingSetPreview = useSelector(selectShowMatchingSetPreview);
  const [hideButtonsBehind, setHideButtonsBehind] = useState(editingMobile);
  const {setPosition} = useEnvelopeContext();
  const dispatch = useDispatch();
  const isLargishAndUp = useIsLargishAndUp();
  const isScreenExtraLarge = useIsExtraLargeAndUp();
  const {matches: isSmallScreenHeight} = useMatchQuery(`(max-height: 608px)`);
  const shouldUseNamedStepsNavMW = shouldUseNamedStepsNav() && !isLargishAndUp;

  const onClickCallbackHandler = useCallback(
    (isEnvelope) => {
      // Only on DYO, we are discarding the active object when clicking on the nav
      // to present the user a "clean" canvas
      if (isFullDyo) {
        setActiveObjects([]);
        canvas?.discardActiveObject();
        canvas?.fire('object:modified');
        canvas?.renderAll();
      }
      cardDesignRef?.current.scrollTo(0, 0);

      if (isEnvelope) {
        setPosition('in-front-of-card');
      } else {
        setPosition('behind-card');
      }
    },
    [cardDesignRef, canvas, setActiveObjects, setPosition, isFullDyo]
  );

  const onClickEnvelopeDesign = useCallback(
    (isEnvelope) => {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {pristineEnvelopeDesign: false},
      });
      storeToolTipToHide(ENVELOPE_TOOLTIP_ID);
      onClickCallbackHandler(isEnvelope);
    },
    [dispatch, onClickCallbackHandler]
  );

  useEffect(() => {
    setHideButtonsBehind(editingMobile);
  }, [editingMobile]);

  const photoButtonText = useMemo(() => {
    if (designHasLogoAndUyo) return 'Photo & Logo';
    if (designHasLogo) return 'Logo';
    return 'Photo';
  }, [designHasLogo, designHasLogoAndUyo]);

  const Toolbar = () => {
    return (
      <>
        {isUyo ? (
          <Button
            onClickCallback={onClickCallbackHandler}
            title={photoButtonText}
            icon={
              <AddPhotosLineIcon color={shouldUseNamedStepsNavMW ? pebble : charcoal} ratio={0.7} />
            }
            section={Sections.UPLOAD_PHOTO}
            editor={isFilledUyo || isLogoFilled ? 'image' : 'intro'}
            disableAutoOpenEditor={isLargishAndUp}
          />
        ) : null}
        {(isUyo && !isFullDyo) || !isUyo ? (
          <Button
            onClickCallback={onClickCallbackHandler}
            title="Text"
            editor="text"
            icon={
              <DocumentIcon color={shouldUseNamedStepsNavMW ? pebble : charcoal} ratio={0.75} />
            }
            section={Sections.CARD_DESIGN}
            disableAutoOpenEditor={isLargishAndUp}
          />
        ) : null}
        <Button
          onClickCallback={onClickCallbackHandler}
          title={isScreenExtraLarge ? 'Background & Effects' : 'Background'}
          icon={
            <DiagonalLinesIcon color={shouldUseNamedStepsNavMW ? pebble : charcoal} ratio={0.75} />
          }
          section={Sections.BACKGROUND}
          highLight={premiumFeaturesWarning}
          premiumFeature
          isPremium={isPremium}
        />
        <Tooltip
          position={isLargishAndUp ? 'right' : 'top'}
          maxContent
          text="NEW: Make any photo or logo a sticker!"
          centerText
          mobileTextSize={!isLargishAndUp}
          visible={pristineEnvelopeDesign && (!isUyo || isLargishAndUp)}
          dataQaId="premium-features-warning-close"
          color="tanzanite"
          shimmer
          id={ENVELOPE_TOOLTIP_ID}
        >
          <Button
            onClickCallback={() => onClickEnvelopeDesign('envelope')}
            title="Envelope"
            icon={<MailIcon color={shouldUseNamedStepsNavMW ? pebble : charcoal} ratio={0.75} />}
            section={Sections.ENVELOPE_LINER}
            highLight={premiumFeaturesWarning}
            premiumFeature
            isPremium={isPremium}
          />
        </Tooltip>
        <Button
          onClickCallback={onClickCallbackHandler}
          title="Reveals"
          icon={<RevealIcon color={shouldUseNamedStepsNavMW ? pebble : charcoal} ratio={0.75} />}
          section={Sections.REVEAL}
          highLight={premiumFeaturesWarning}
          premiumFeature
          isPremium={isPremium}
        />
      </>
    );
  };

  const ClassicToolbar = () => (
    <div
      className={cx({
        'card-design__nav-bottom__panel': true,
        'card-design__nav-bottom__uyo': isUyo,
        vertical: !!vertical,
        behind: hideButtonsBehind,
        'init-hidden': !isPremium && showMatchingSetPreview,
        'card-design__vertical-nav--remove-gap': isSmallScreenHeight,
      })}
      data-qa-id="card-design-navigator"
      ref={containerRef}
    >
      <Toolbar />
    </div>
  );
  const FloatingToolbar = () => (
    <div className="card-design-nav__bottom-toolbar">
      <BottomToolbar>
        <Toolbar />
      </BottomToolbar>
    </div>
  );

  return (
    <CSSTransition
      nodeRef={containerRef}
      in={!isPremium && showMatchingSetPreview}
      classNames="hide-for-mset-preview"
      timeout={{appear: 500}}
    >
      {isLargishAndUp || !shouldUseNamedStepsNavMW ? <ClassicToolbar /> : <FloatingToolbar />}
    </CSSTransition>
  );
};
